import { useCallback, useEffect, useRef, useState } from "react";
import { ensureJwtUsable } from "../services/AbstractService";

const getFullKeyName = (parentName, keyName) => `${parentName}.${keyName}`;

const getTimedDefaultEntry = (defaultValue) => ({
  value: defaultValue,
  updatedAt: Date.now(),
});

const isTimedEntryExpired = (entry, timeoutMinutes) => {
  if (timeoutMinutes == null || timeoutMinutes <= 0) {
    return false;
  }

  if (entry == null || entry.updatedAt == null) {
    return true;
  }

  const timeoutMs = timeoutMinutes * 60 * 1000;
  return Date.now() - entry.updatedAt > timeoutMs;
};

export const useLocalStorage = (parentName, keyName, defaultValue=null) => {
  const fullKeyName = getFullKeyName(parentName, keyName);

  const [storedValue, setStoredValue] = useState(() => {
    try {
      const value = window.localStorage.getItem(fullKeyName);

      if (value != null) {
        return JSON.parse(value);
      } else {
        window.localStorage.setItem(fullKeyName, JSON.stringify(defaultValue));

        return defaultValue;
      }
    } catch (err) {
      return defaultValue;
    }
  });

  const setValue = (newValue) => {
    try {
      window.localStorage.setItem(fullKeyName, JSON.stringify(newValue));
    } catch (err) {}

    setStoredValue(newValue);
  };

  return [storedValue, setValue];
};

export const useLocalStorageWithTimeout = (
  parentName,
  keyName,
  defaultValue = null,
  timeoutMinutes = null,
) => {
  const fullKeyName = getFullKeyName(parentName, keyName);

  const [storedValue, setStoredValue] = useState(() => {
    try {
      const rawValue = window.localStorage.getItem(fullKeyName);

      if (rawValue != null) {
        const timedEntry = JSON.parse(rawValue);

        if (isTimedEntryExpired(timedEntry, timeoutMinutes)) {
          window.localStorage.setItem(
            fullKeyName,
            JSON.stringify(getTimedDefaultEntry(defaultValue)),
          );

          return defaultValue;
        }

        return timedEntry.value;
      }

      window.localStorage.setItem(
        fullKeyName,
        JSON.stringify(getTimedDefaultEntry(defaultValue)),
      );

      return defaultValue;
    } catch (err) {
      return defaultValue;
    }
  });

  const setValue = (newValue) => {
    try {
      window.localStorage.setItem(
        fullKeyName,
        JSON.stringify(getTimedDefaultEntry(newValue)),
      );
    } catch (err) {}

    setStoredValue(newValue);
  };

  return [storedValue, setValue];
};

export const useJwtStorage = (defaultValue = null) => {
  const [jwt, setStoredJwt] = useLocalStorage("Auth", "jwt", defaultValue);
  const jwtRef = useRef(jwt);

  useEffect(() => {
    jwtRef.current = jwt;
  }, [jwt]);

  const setJwt = useCallback(
    (newValue) => {
      jwtRef.current = newValue;
      setStoredJwt(newValue);
    },
    [setStoredJwt],
  );

  const refreshJwt = useCallback(async () => {
    const currentJwt = jwtRef.current ?? jwt;
    if (!currentJwt) {
      return null;
    }

    const refreshedJwt = await ensureJwtUsable(currentJwt, (nextJwt) => {
      jwtRef.current = nextJwt;
      setStoredJwt(nextJwt);
    });

    if (refreshedJwt && refreshedJwt !== jwtRef.current) {
      jwtRef.current = refreshedJwt;
      setStoredJwt(refreshedJwt);
    }

    return refreshedJwt;
  }, [jwt, setStoredJwt]);

  useEffect(() => {
    let isMounted = true;

    if (!jwt) {
      return undefined;
    }

    (async () => {
      const nextJwt = await ensureJwtUsable(jwt, (newJwt) => {
        if (!isMounted) {
          return;
        }

        jwtRef.current = newJwt;
        setStoredJwt(newJwt);
      });

      if (!isMounted) {
        return;
      }

      if (nextJwt && nextJwt !== jwt) {
        jwtRef.current = nextJwt;
        setStoredJwt(nextJwt);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [jwt, setStoredJwt]);

  return [jwt, setJwt, refreshJwt];
};

export const clearLocalStorage = () => {
  window.localStorage.clear();
};
